import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";

// Upload + server-side parsing/matching can legitimately take a while, but it
// should never run forever. Without a client-side timeout, a stalled backend
// request (e.g. a database query hanging mid-flight) leaves `fetch` pending
// indefinitely, so the upload spinner spins forever. This bounds it: the request
// always resolves — either with a result or a clear "try again" error.
const UPLOAD_TIMEOUT_MS = 120_000;

async function uploadWithTimeout(url: string, file: File): Promise<Response> {
  const formData = new FormData();
  formData.append("file", file);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  try {
    return await fetch(url, {
      method: "POST",
      body: formData,
      credentials: "include",
      signal: controller.signal,
    });
  } catch (err: any) {
    if (err?.name === "AbortError") {
      throw new Error(
        "The upload timed out — the server took too long to respond. Please wait a moment and try again.",
      );
    }
    throw new Error("Network error while uploading. Please check your connection and try again.");
  } finally {
    clearTimeout(timeoutId);
  }
}

export function useStatementUpload() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const res = await uploadWithTimeout("/api/statements/upload", file);

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(error.error || "Failed to upload statement");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      toast({ title: "Statement uploaded and parsed successfully!" });
    },
    onError: (error: Error) => {
      toast({ 
        title: "Upload failed", 
        description: error.message, 
        variant: "destructive" 
      });
    },
  });
}

export function useReceiptUpload() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (file: File) => {
      const res = await uploadWithTimeout("/api/receipts/upload", file);

      if (res.status === 409) {
        const data = await res.json().catch(() => ({ error: "Duplicate file" }));
        toast({
          title: "Duplicate skipped",
          description: `"${file.name}" — ${data.error}`,
        });
        return { skipped: true };
      }

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(error.error || "Failed to upload receipt");
      }

      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      if (!data?.skipped) {
        toast({ title: "Receipt uploaded and parsed successfully!" });
      }
    },
    onError: (error: Error) => {
      toast({ 
        title: "Upload failed", 
        description: error.message, 
        variant: "destructive" 
      });
    },
  });
}

export function useReparseAllReceipts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/receipts/reparse-all", {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Re-parse failed" }));
        throw new Error(error.error || "Failed to re-parse receipts");
      }

      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      toast({ title: `Re-parsed ${data.reparsed} receipts successfully` });
    },
    onError: (error: Error) => {
      toast({ title: "Re-parse failed", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteReceipt() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/receipts/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Delete failed" }));
        throw new Error(error.error || "Failed to delete receipt");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      toast({ title: "Receipt deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteStatement() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`/api/statements/${id}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Delete failed" }));
        throw new Error(error.error || "Failed to delete statement");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      toast({ title: "Statement deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
    },
  });
}

export function useUnmatchEntry() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ statementId, entryId }: { statementId: number; entryId: number }) => {
      const res = await fetch(`/api/statements/${statementId}/entries/${entryId}/unmatch`, {
        method: "POST",
        credentials: "include",
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Unmatch failed" }));
        throw new Error(error.error || "Failed to remove match");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      toast({ title: "Match removed successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Unmatch failed", description: error.message, variant: "destructive" });
    },
  });
}

export function useDeleteAllReceipts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/receipts/clear-all", {
        method: "DELETE",
        credentials: "include",
      });

      if (!res.ok) {
        const error = await res.json().catch(() => ({ error: "Delete all failed" }));
        throw new Error(error.error || "Failed to delete all receipts");
      }

      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/receipts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/statements"] });
      toast({ title: "All receipts deleted" });
    },
    onError: (error: Error) => {
      toast({ title: "Delete all failed", description: error.message, variant: "destructive" });
    },
  });
}
